/**
 * Backend-issued S3 access. Replaces `@aws-amplify/storage` (`Storage.put`,
 * `Storage.get`, `Storage.list`, `Storage.remove`) so the browser no longer
 * needs Cognito Identity Pool IAM credentials to talk to S3 directly.
 *
 * Backend endpoints are in `myassetmap-legacy-api/routes/s3presign.js`.
 *
 * Key prefixing: legacy call sites all used `level: 'public'` which had
 * Amplify silently prepend `public/`. This module preserves that behavior by
 * default so the migration is a drop-in swap. Pass `prefix: 'none'` to send
 * the raw key.
 */
import environment from '_environments'
import { apis } from '@/config/apiConfig'
import { apiFetch } from './api'

const SINGLE_UPLOAD_MAX_BYTES = 100 * 1024 * 1024 // 100 MB
const MULTIPART_PART_SIZE_BYTES = 25 * 1024 * 1024 // 25 MB per part
const PUBLIC_PREFIX = 'public/'

type KeyPrefix = 'public' | 'none'

export interface ProgressEvent {
  loaded: number
  total: number
}

export interface UploadOptions {
  contentType?: string
  prefix?: KeyPrefix
  onProgress?: (event: ProgressEvent) => void
}

export interface PresignedPost {
  url: string
  fields: Record<string, string>
}

/** Matches the legacy `Storage.list` item shape so call sites stay drop-in. */
export interface ListedObject {
  key: string
  size: number
  lastModified: string
  eTag: string
}

interface BackendListResult {
  objects: { key: string; size: number; lastModified: string; etag: string }[]
  prefixes: string[]
  isTruncated: boolean
  nextContinuationToken: string | null
}

export interface ListPage {
  objects: ListedObject[]
  prefixes: string[]
  isTruncated: boolean
  nextContinuationToken: string | null
}

interface BackendResponse<T> {
  success?: boolean
  message?: string
  data?: T
}

const resolveKey = (key: string, prefix: KeyPrefix = 'public'): string => {
  if (prefix === 'none') return key
  return key.startsWith(PUBLIC_PREFIX) ? key : `${PUBLIC_PREFIX}${key}`
}

const apiUrl = (path: string): string => `${apis.apiDatabase.uri}${path}`

const callBackend = async <T>(
  path: string,
  body: Record<string, unknown>
): Promise<T> => {
  const result = (await apiFetch(
    apiUrl(path),
    'POST',
    body
  )) as BackendResponse<T>
  if (!result.success) {
    throw new Error(result.message || `Backend call to ${path} failed`)
  }
  if (result.data === undefined) {
    throw new Error(`Backend call to ${path} returned no data`)
  }
  return result.data
}

/**
 * Direct, anonymous-readable URL. No backend round trip, no presigning. Use
 * this for assets that should be publicly cacheable (e.g. profile images)
 * once the bucket policy permits `GetObject` on the prefix.
 */
export const getPublicUrl = (
  key: string,
  options: { prefix?: KeyPrefix } = {}
): string => {
  const fullKey = resolveKey(key, options.prefix)
  const region = environment.aws.region || 'us-east-1'
  const bucket = environment.aws.s3Bucket
  const host =
    region === 'us-east-1'
      ? `${bucket}.s3.amazonaws.com`
      : `${bucket}.s3.${region}.amazonaws.com`
  const encoded = fullKey.split('/').map(encodeURIComponent).join('/')
  return `https://${host}/${encoded}`
}

/**
 * Short-lived presigned GET URL. Use for non-public assets or anything the
 * browser needs auth'd access to.
 */
export const getPresignedUrl = async (
  key: string,
  options: { expiresIn?: number; prefix?: KeyPrefix } = {}
): Promise<string> => {
  const fullKey = resolveKey(key, options.prefix)
  const data = await callBackend<{ url: string }>('s3/presign-get', {
    key: fullKey,
    expiresIn: options.expiresIn,
  })
  return data.url
}

/**
 * Upload a Blob/File to S3. Files <=100 MB use a presigned POST in one
 * request; larger files use multipart with 25 MB parts.
 */
export const upload = async (
  key: string,
  file: Blob,
  options: UploadOptions = {}
): Promise<{ key: string }> => {
  const fullKey = resolveKey(key, options.prefix)
  const contentType =
    options.contentType || file.type || 'application/octet-stream'

  if (file.size <= SINGLE_UPLOAD_MAX_BYTES) {
    return singleUpload(fullKey, file, contentType, options.onProgress)
  }
  return multipartUpload(fullKey, file, contentType, options.onProgress)
}

const singleUpload = async (
  fullKey: string,
  file: Blob,
  contentType: string,
  onProgress?: UploadOptions['onProgress']
): Promise<{ key: string }> => {
  const policy = await callBackend<PresignedPost>('s3/presign-put', {
    key: fullKey,
    contentType,
  })

  const form = new FormData()
  Object.entries(policy.fields).forEach(([k, v]) => form.append(k, v))
  form.append('file', file)

  if (onProgress) {
    await xhrPostWithProgress(policy.url, form, onProgress)
  } else {
    const response = await fetch(policy.url, { method: 'POST', body: form })
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`)
    }
  }

  return { key: fullKey }
}

const multipartUpload = async (
  fullKey: string,
  file: Blob,
  contentType: string,
  onProgress?: UploadOptions['onProgress']
): Promise<{ key: string }> => {
  const partCount = Math.ceil(file.size / MULTIPART_PART_SIZE_BYTES)

  const { uploadId } = await callBackend<{ uploadId: string; key: string }>(
    's3/multipart/create',
    { key: fullKey, contentType }
  )

  try {
    const { parts: signedParts } = await callBackend<{
      parts: { url: string; partNumber: number }[]
    }>('s3/multipart/sign-part', { key: fullKey, uploadId, partCount })

    const completedParts: { PartNumber: number; ETag: string }[] = []
    let bytesUploaded = 0

    for (const { url, partNumber } of signedParts) {
      const start = (partNumber - 1) * MULTIPART_PART_SIZE_BYTES
      const end = Math.min(start + MULTIPART_PART_SIZE_BYTES, file.size)
      const partBlob = file.slice(start, end)

      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(url, { method: 'PUT', body: partBlob })
      if (!response.ok) {
        throw new Error(
          `Multipart PUT failed: part ${partNumber}, status ${response.status}`
        )
      }
      const etag = response.headers.get('ETag')
      if (!etag) {
        // Bucket CORS must expose ETag for the browser to read it back.
        throw new Error(
          `Part ${partNumber} missing ETag — verify bucket CORS exposes the ETag header`
        )
      }

      completedParts.push({ PartNumber: partNumber, ETag: etag })
      bytesUploaded += partBlob.size
      onProgress?.({ loaded: bytesUploaded, total: file.size })
    }

    await callBackend('s3/multipart/complete', {
      key: fullKey,
      uploadId,
      parts: completedParts,
    })

    return { key: fullKey }
  } catch (error) {
    // Best-effort abort; swallow the abort error so we surface the original failure.
    apiFetch(apiUrl('s3/multipart/abort'), 'POST', {
      key: fullKey,
      uploadId,
    }).catch(() => undefined)
    throw error
  }
}

const xhrPostWithProgress = (
  url: string,
  form: FormData,
  onProgress: (event: ProgressEvent) => void
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        onProgress({ loaded: event.loaded, total: event.total })
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status}`))
      }
    })
    xhr.addEventListener('error', () =>
      reject(new Error('S3 upload network error'))
    )
    xhr.addEventListener('abort', () => reject(new Error('S3 upload aborted')))
    xhr.open('POST', url)
    xhr.send(form)
  })

/**
 * Drop-in replacement for `Storage.list(prefix)` — returns the raw array of
 * objects (matches Amplify v1 shape: `[{ key, eTag, lastModified, size }]`).
 * The `public/` prefix is stripped from returned keys when `prefix: 'public'`
 * (the default), matching what Amplify did with `level: 'public'`.
 */
export const list = async (
  prefix: string,
  options: { prefix?: KeyPrefix } = {}
): Promise<ListedObject[]> => {
  const page = await listPage(prefix, options)
  return page.objects
}

/** Full paginated S3 list — use when you need >1000 keys. */
export const listPage = async (
  prefix: string,
  options: {
    continuationToken?: string
    maxKeys?: number
    prefix?: KeyPrefix
  } = {}
): Promise<ListPage> => {
  const keyPrefix: KeyPrefix = options.prefix ?? 'public'
  const fullPrefix = resolveKey(prefix, keyPrefix)
  const raw = await callBackend<BackendListResult>('s3/list', {
    prefix: fullPrefix,
    continuationToken: options.continuationToken,
    maxKeys: options.maxKeys,
  })

  const stripPrefix = keyPrefix === 'public' ? PUBLIC_PREFIX : ''
  const stripFromKey = (k: string): string =>
    stripPrefix && k.startsWith(stripPrefix) ? k.slice(stripPrefix.length) : k

  return {
    objects: raw.objects.map(o => ({
      key: stripFromKey(o.key),
      size: o.size,
      lastModified: o.lastModified,
      eTag: o.etag,
    })),
    prefixes: raw.prefixes.map(p => stripFromKey(p)),
    isTruncated: raw.isTruncated,
    nextContinuationToken: raw.nextContinuationToken,
  }
}

export const remove = async (
  keys: string | string[],
  options: { prefix?: KeyPrefix } = {}
): Promise<{
  deleted: string[]
  errors: { key: string; code: string; message: string }[]
}> => {
  const keyList = (Array.isArray(keys) ? keys : [keys]).map(k =>
    resolveKey(k, options.prefix)
  )
  return callBackend<{
    deleted: string[]
    errors: { key: string; code: string; message: string }[]
  }>('s3/delete', { keys: keyList })
}

export const copy = async (
  from: string,
  to: string,
  options: { prefix?: KeyPrefix } = {}
): Promise<{ from: string; to: string }> => {
  return callBackend<{ from: string; to: string }>('s3/copy', {
    from: resolveKey(from, options.prefix),
    to: resolveKey(to, options.prefix),
  })
}

/**
 * Rename a file or folder. For folders the backend handles the recursive
 * copy-then-delete so the browser makes a single round-trip regardless of
 * how many objects are nested inside.
 */
export const rename = async (
  oldKey: string,
  newKey: string,
  options: { prefix?: KeyPrefix } = {}
): Promise<{ deleted: string[]; errors: { key: string; code: string; message: string }[] }> => {
  return callBackend<{
    deleted: string[]
    errors: { key: string; code: string; message: string }[]
  }>('s3/rename', {
    oldKey: resolveKey(oldKey, options.prefix),
    newKey: resolveKey(newKey, options.prefix),
  })
}

/**
 * Used by the API layer to inflate large responses the backend has dehydrated
 * to S3. Backend writes the object at `public/{key}` and returns just `{key}`
 * in the response — we presign, fetch, and remove.
 */
export const hydrateResponse = async (hydrateKey: string): Promise<unknown> => {
  const url = await getPresignedUrl(hydrateKey)
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch hydrated object: ${response.status}`)
  }
  const text = await response.text()

  // Fire-and-forget cleanup; failures here are non-fatal.
  remove(hydrateKey).catch(error => {
    console.warn('Failed to remove hydrated object after fetch', error)
  })

  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

export const STORAGE_LIMITS = {
  SINGLE_UPLOAD_MAX_BYTES,
  MULTIPART_PART_SIZE_BYTES,
}
